import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import sellarModule from '../../../api/modules/seller.module';
import adminModule from '../../../api/modules/admin.module';
import { useListingSuggestedPrice } from '../../../hooks/useListingSuggestedPrice';
import { computeBuyerPrice, priceBreakdown } from '../../../utils/listingPricing';
import EditListingModalView from './EditListingModal.view';

// Local rounding helper for the mode-switch prefill math below. Mirrors the
// (unexported) round2 in utils/listingPricing.js.
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// The two condition vocabularies. Sealed product is graded "Sealed" and single
// cards take a play grade; the server rejects the wrong one on save (#108), so
// the modal must offer the right one or the seller dead-ends in a 400.
const STANDARD_CONDITIONS = [
  'Near Mint',
  'Lightly Played',
  'Moderately Played',
  'Heavily Played',
  'Damaged',
];
const SEALED_CONDITIONS = ['Sealed'];

/**
 * Edit listing. The container.
 *
 * Owns the form state, the two queries and the update request. Nothing about
 * any of them changed in the conversion: same query keys, same
 * `sellarModule.updateCardListing(FormData)` call, same keys appended in the
 * same order, same validation, same `onUpdate(result.data)` then `onClose()`
 * on success. The prop API and the file path are unchanged too, so
 * `ListingTable` needs no edit.
 *
 * The markup is now in `EditListingModal.view.jsx`, which is pure and is what
 * the state explorer renders.
 */
const EditListingModal = ({ isOpen, onClose, listing, onUpdate }) => {
  const [formData, setFormData] = useState({
    list_id: '',
    card_id: '',
    condition: '',
    description: '',
    price: '',
    quantity: '',
    address: '',
    processing_days: '',
    images: [],
    existingImages: [],
    bought_for_price: '',
    pricing_mode: 'fixed',
    fee_frame: 'inclusive',
    markup_percent: '',
    price_floor: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // This product's vocabulary, plus whatever this listing actually is.
  //
  // The listing's own condition has to be in the list or the select renders
  // blank and saving rewrites it — silently changing the priced basis of a
  // tracked listing. That covers "Damaged" (which this hard-coded list never
  // held, though the create wizard has always offered it) and a legacy sealed
  // listing still stored on a play grade: the seller sees the current value AND
  // can pick the valid one, which is exactly what the server's grandfathering
  // leaves them room to do. Issue #108.
  const baseConditions = listing?.is_sealed ? SEALED_CONDITIONS : STANDARD_CONDITIONS;
  const conditionOptions = baseConditions
    .concat(
      listing?.condition && !baseConditions.includes(listing.condition) ? [listing.condition] : []
    )
    .map((value) => ({ value, label: value }));

  // Fetch system settings to get dynamic platform fee (same source as
  // CreateListing's PricingQuantityStep).
  const { data: systemSettings } = useQuery({
    queryKey: ['systemSettings'],
    queryFn: async () => {
      const result = await adminModule.getSystemSettings();
      if (result.success) {
        return result.data;
      }
      return { sell_in_person: false, platform_fee: 5 };
    },
    staleTime: 5 * 60 * 1000,
  });

  const platformFee = systemSettings?.platform_fee || 5;
  const feeRate = (platformFee || 0) / 100;

  // Fresh, finish-aware market price for this listing's card. Condition
  // changes with the form; finish is not editable here so it comes straight
  // from the listing.
  const { data: suggested } = useListingSuggestedPrice(
    listing?.card_id?.trim() || null,
    listing?.finish,
    formData.condition
  );
  const marketPrice = suggested && !suggested.stale && suggested.price > 0 ? suggested.price : null;

  useEffect(() => {
    if (listing && isOpen) {
      setFormData({
        list_id: listing.list_id || '',
        card_id: listing.card_id || '',
        condition: listing.condition || '',
        description: listing.description || '',
        price: listing.price?.toString() || '',
        quantity: listing.qty?.toString() || '',
        address: listing.address || '',
        processing_days: listing.processing_days?.toString() || '',
        images: [],
        existingImages: listing.image_url,
        bought_for_price: listing.bought_price?.toString() || '',
        pricing_mode: listing.pricing_mode || 'fixed',
        fee_frame: listing.fee_frame || 'inclusive',
        markup_percent: listing.markup_percent ?? '',
        price_floor: listing.price_floor ?? '',
      });
      setErrors({});
    }
  }, [listing, isOpen]);

  // Live buyer-price preview. Source of truth is computeBuyerPrice, never
  // derive it locally. The backend recomputes this authoritatively on save
  // (tracked mode recomputes from the live market price; fixed mode uses the
  // submitted price as-is).
  const buyer = computeBuyerPrice({
    mode: formData.pricing_mode,
    frame: formData.fee_frame,
    markupPercent: formData.markup_percent,
    priceFloor: formData.price_floor,
    fixedValue: formData.price,
    marketPrice,
    feeRate,
  });
  const bd = priceBreakdown(buyer ?? 0, feeRate);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // `ModalSelect` reports the raw value on both tracks, so the synthetic event
  // the shared handler expects is built here rather than in the view.
  const handleConditionChange = (value) => {
    handleInputChange({ target: { name: 'condition', value } });
  };

  const handleMarkupChange = (e) => {
    let value = e.target.value.replace(/,/g, '.');
    if (value !== '' && !/^-?\d*\.?\d*$/.test(value)) return;
    setFormData((prev) => ({ ...prev, markup_percent: value }));
    if (errors.markup_percent) {
      setErrors((prev) => ({ ...prev, markup_percent: '' }));
    }
  };

  const handleFloorChange = (e) => {
    let value = e.target.value.replace(/,/g, '.');
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    setFormData((prev) => ({ ...prev, price_floor: value }));
  };

  const handleFixedValueChange = (e) => {
    let value = e.target.value.replace(/,/g, '.');
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    setFormData((prev) => ({ ...prev, price: value }));
    if (errors.price) {
      setErrors((prev) => ({ ...prev, price: '' }));
    }
  };

  // Mode-switch prefill so the seller doesn't see a jarring jump when
  // toggling between fixed and tracked pricing.
  const handlePricingModeChange = (nextMode) => {
    if (nextMode === formData.pricing_mode) return;

    if (nextMode === 'tracked') {
      // Fixed -> Tracked: back-solve a markup from the current price and
      // market price, and default the floor to the current price.
      const currentPrice = parseFloat(formData.price);
      let nextMarkup = formData.markup_percent;
      if (marketPrice > 0 && !isNaN(currentPrice)) {
        nextMarkup = round2((currentPrice / marketPrice - 1) * 100);
      }
      const nextFloor = !isNaN(currentPrice) ? currentPrice : formData.price_floor;
      setFormData((prev) => ({
        ...prev,
        pricing_mode: 'tracked',
        markup_percent: nextMarkup,
        price_floor: nextFloor,
      }));
    } else {
      // Tracked -> Fixed: seed the fixed input with the current derived
      // buyer price so it does not jump.
      setFormData((prev) => ({
        ...prev,
        pricing_mode: 'fixed',
        price: buyer != null ? buyer.toFixed(2) : prev.price,
      }));
    }
  };

  const handleFeeFrameChange = (nextFrame) => {
    setFormData((prev) => ({ ...prev, fee_frame: nextFrame }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    const totalImagesCount = formData.existingImages.length + formData.images.length + files.length;
    if (totalImagesCount > 5) {
      setErrors((prev) => ({
        ...prev,
        images: `Maximum 5 images allowed. You can add ${
          5 - formData.existingImages.length - formData.images.length
        } more.`,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }));

    // Clear any previous errors
    if (errors.images) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.images;
        return newErrors;
      });
    }
  };

  const removeNewImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const removeExistingImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      existingImages: prev.existingImages.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.condition) newErrors.condition = 'Condition is required';
    if (!formData.description) newErrors.description = 'Description is required';

    if (formData.pricing_mode === 'tracked') {
      if (
        formData.markup_percent === '' ||
        formData.markup_percent == null ||
        isNaN(formData.markup_percent)
      ) {
        newErrors.markup_percent = 'Valid markup is required';
      }
      if (buyer === null) {
        newErrors.price = 'No market price available, use a fixed price';
      }
    } else if (!formData.price || isNaN(formData.price) || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }

    if (!formData.quantity || isNaN(formData.quantity) || parseInt(formData.quantity) <= 0) {
      newErrors.quantity = 'Valid quantity is required';
    }
    if (!formData.address) newErrors.address = 'Address is required';
    if (
      !formData.processing_days ||
      isNaN(formData.processing_days) ||
      parseInt(formData.processing_days) <= 0
    ) {
      newErrors.processing_days = 'Valid processing days is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // `ModalFormBody` already calls preventDefault on the submit event.
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('list_id', formData.list_id);
      submitData.append('condition', formData.condition);
      submitData.append('description', formData.description);
      // Backend is authoritative and recomputes tracked prices from the live
      // market price, so what's sent for tracked mode doesn't matter, so the
      // derived buyer price is sent for convenience/back-compat.
      // FIXED mode: submit the RAW seller-entered value, not the grossed-up
      // buyer price, because the backend does its own frame gross-up from this raw
      // value, so submitting `buyer` here would double-gross fixed+exclusive
      // listings (see apps/cards/pricing.py compute_buyer_price).
      submitData.append(
        'price',
        formData.pricing_mode === 'fixed'
          ? (formData.price ?? '')
          : buyer != null
            ? buyer.toFixed(2)
            : '0'
      );
      submitData.append('quantity', formData.quantity);
      submitData.append('address', formData.address);
      submitData.append('processing_days', formData.processing_days);
      submitData.append('pricing_mode', formData.pricing_mode);
      submitData.append('fee_frame', formData.fee_frame);

      if (formData.pricing_mode === 'tracked') {
        // CRITICAL: the backend clears price_floor in tracked mode if the
        // request omits it, so always resend both, even when unchanged.
        submitData.append('markup_percent', formData.markup_percent);
        submitData.append(
          'price_floor',
          formData.price_floor === '' || formData.price_floor == null ? '' : formData.price_floor
        );
      }

      // Append bought_for_price if provided (optional field)
      if (formData.bought_for_price && formData.bought_for_price.trim() !== '') {
        submitData.append('bought_for_price', formData.bought_for_price.toString());
      }

      // Add existing image URLs
      formData.existingImages.forEach((url) => {
        submitData.append('images', url); // string
      });

      // Add new image files
      formData.images.forEach((file) => {
        submitData.append('images', file); // file
      });

      const result = await sellarModule.updateCardListing(submitData);

      if (result.success) {
        onUpdate(result.data);
        onClose();
      } else {
        setErrors({ submit: result.error });
      }
    } catch (error) {
      setErrors({ submit: 'An unexpected error occurred' });
    } finally {
      setLoading(false);
    }
  };

  if (!listing) return null;

  // Object URLs for the not-yet-uploaded files, so the view never touches a
  // `File`. Computed here, exactly where `URL.createObjectURL` was called
  // before, so nothing about when a preview is created has changed.
  const newImages = formData.images.map((file) => ({
    name: file.name,
    url: URL.createObjectURL(file),
  }));

  return (
    <EditListingModalView
      open={isOpen}
      cardName={listing.card}
      conditionOptions={conditionOptions}
      condition={formData.condition}
      onConditionChange={handleConditionChange}
      description={formData.description}
      pricingMode={formData.pricing_mode}
      onPricingModeChange={handlePricingModeChange}
      feeFrame={formData.fee_frame}
      onFeeFrameChange={handleFeeFrameChange}
      markupPercent={formData.markup_percent}
      onMarkupChange={handleMarkupChange}
      priceFloor={formData.price_floor}
      onFloorChange={handleFloorChange}
      price={formData.price}
      onFixedValueChange={handleFixedValueChange}
      platformFee={platformFee}
      buyer={buyer}
      breakdown={bd}
      quantity={formData.quantity}
      boughtForPrice={formData.bought_for_price}
      address={formData.address}
      processingDays={formData.processing_days}
      existingImages={formData.existingImages || []}
      newImages={newImages}
      onInputChange={handleInputChange}
      onFilesSelected={handleFileChange}
      onRemoveExistingImage={removeExistingImage}
      onRemoveNewImage={removeNewImage}
      errors={errors}
      submitting={loading}
      onClose={onClose}
      onSubmit={handleSubmit}
    />
  );
};

export default EditListingModal;
